import React, {useState} from "react";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Grid,
    IconButton,
    Box,
    Typography
} from "@mui/material";
import Loading from "../components/common/Loading";
import {useTranslation} from "react-i18next";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { EMAIL_TEMPLATES, DELETE_EMAIL_TEMPLATE } from "../gql/emailTemplates.gql";
import AppSnackbar from "../common/AppSnackbar";
import {useNavigate} from "react-router-dom";
import AlertDialogSlide from "../components/common/AlertDialogSlide";

const EmailsConfiguration = () => {
    const {t} = useTranslation();
    const { data, loading, error } = useQuery(EMAIL_TEMPLATES);
    const [removeTemplate] = useMutation(DELETE_EMAIL_TEMPLATE, {
        refetchQueries: [{ query: EMAIL_TEMPLATES }],
        awaitRefetchQueries: true
    });

    const templates = data?.emailTemplates ?? [];

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [severity, setSeverity] = useState("success");
    const navigate = useNavigate();
    // Alert dialog state
    const [openAlert, setOpenAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [alertAction, setAlertAction] = useState("");
    const [pendingId, setPendingId] = useState(null);

    const resetAlert = () => {
        setOpenAlert(false);
        setAlertTitle("");
        setAlertMessage("");
        setAlertAction("");
        setPendingId(null);
    }

    const handleDelete = async (id) => {
        setPendingId(id);
        setAlertTitle(t('confirm'));
        setAlertMessage('removeThisTemplate');
        setAlertAction('DELETE_TEMPLATE');
        setOpenAlert(true);
    };

    const handleCloseAlert = async (result, action) => {
        setOpenAlert(false);
        if (result && action === 'DELETE_TEMPLATE' && pendingId != null) {
            try {
                await removeTemplate({ variables: { id: pendingId } });
                setSnackbarMessage('templateRemoved');
                setSeverity("success");
                setOpenSnackbar(true);
            } catch {
                setSnackbarMessage("removeFailed");
                setSeverity("error");
                setOpenSnackbar(true);
            } finally {
                resetAlert();
            }
        } else {
            resetAlert();
        }
    }

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} wrap="nowrap">
                <Grid item>
                    <Typography variant="h4" component="h2">{t('emailTemplates')}</Typography>
                </Grid>
            </Grid>

            <Paper variant="outlined">
                {loading ? (
                    <Box sx={{ p: 6, textAlign: "center" }}>Laden…</Box>
                ) : error ? (
                    <Box sx={{ p: 6, textAlign: "center" }}>Fout bij laden</Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('type')}</TableCell>
                                <TableCell>{t('replyTo')}</TableCell>
                                <TableCell>{t('subject')}</TableCell>
                                <TableCell>{t('preview')}</TableCell>
                                <TableCell align="center">{t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {templates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Box sx={{ p: 6, textAlign: "center" }}>
                                            <Typography color="text.secondary">
                                                Nog geen templates. Klik op <b>Nieuwe template</b>.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : templates.map(template => (
                                <TableRow key={template.id} hover>
                                    <TableCell>{t(template.emailType)}</TableCell>
                                    <TableCell>{template.replyTo || "-"}</TableCell>
                                    <TableCell sx={{ maxWidth: 300, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {template.subject}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {template.body}
                                    </TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                        <IconButton color="primary" onClick={() => navigate(`emails/${template.id}?type=${template.emailType}`)} data-testid={`edit-email-template-${template.emailType}`}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(template.id)} data-testid={`delete-email-template-${template.emailType}`}><Delete /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <AppSnackbar
                openSnackbar={openSnackbar}
                snackbarMessage={snackbarMessage}
                setOpenSnackbar={setOpenSnackbar}
                severity={severity}
            />

            <AlertDialogSlide
                title={alertTitle}
                message={alertMessage}
                openAlert={openAlert}
                action={alertAction}
                onClose={handleCloseAlert}
            />
        </Box>
    );
};

export default withAuthenticationRequired(EmailsConfiguration, {
    onRedirecting: () => <Loading/>,
    returnTo: "/beheer/login"
});
